import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc } from 'firebase/firestore';
import { db, auth } from '../../firebase';

function ChartOfAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [balances, setBalances] = useState({});
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const user = auth.currentUser;

  // Sort function for ledger codes
  const sortByLedger = (items) => {
    return items.sort((a, b) => {
      const getLedgerNumber = (ledger) => {
        if (!ledger) return 0;
        const lastFour = ledger.slice(-4);
        return parseInt(lastFour, 10) || 0;
      };
      return getLedgerNumber(a.Ledger) - getLedgerNumber(b.Ledger);
    });
  };

  // Load balances
  useEffect(() => {
    if (!user) return;
    const balancesRef = doc(db, 'users', user.uid, 'balances', 'balances');
    const unsubscribe = onSnapshot(balancesRef, (snapshot) => {
      if (snapshot.exists()) {
        setBalances(snapshot.data());
      }
    });
    return unsubscribe;
  }, [user]);

  // Load accounts
  useEffect(() => {
    if (!user) return;
    const accountsRef = collection(db, 'users', user.uid, 'account_setup');
    const unsubscribe = onSnapshot(accountsRef, (snapshot) => {
      const accountsData = snapshot.docs.map(doc => ({ id: doc.id, type: 'account', ...doc.data() }));
      setAccounts(sortByLedger(accountsData));
    });
    return unsubscribe;
  }, [user]);

  // Load income categories
  useEffect(() => {
    if (!user) return;
    const q = collection(db, 'users', user.uid, 'income_categories');
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const categoriesData = snapshot.docs.map(doc => ({ id: doc.id, type: 'income', ...doc.data() }));
      setIncomeCategories(sortByLedger(categoriesData));
    });
    return unsubscribe;
  }, [user]);

  // Load expense categories
  useEffect(() => {
    if (!user) return;
    const q = collection(db, 'users', user.uid, 'expense_categories');
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const categoriesData = snapshot.docs.map(doc => ({ id: doc.id, type: 'expense', ...doc.data() }));
      setExpenseCategories(sortByLedger(categoriesData));
    });
    return unsubscribe;
  }, [user]);

  // Combine all items and sort by ledger
  const allItems = sortByLedger([...accounts, ...incomeCategories, ...expenseCategories]);

  // Apply filters
  const filteredItems = allItems.filter(item => {
    // Type filter
    if (typeFilter !== 'all' && item.type !== typeFilter) {
      return false;
    }

    // Search filter (search in ledger code or name)
    if (searchText.trim() !== '') {
      const searchLower = searchText.toLowerCase();
      const ledgerMatch = (item.Ledger || '').toLowerCase().includes(searchLower);
      const nameMatch = (item.name || '').toLowerCase().includes(searchLower);
      return ledgerMatch || nameMatch;
    }

    return true;
  });

  const getTypeLabel = (type) => {
    switch(type) {
      case 'account': return 'Account';
      case 'income': return 'Income';
      case 'expense': return 'Expense';
      default: return 'Unknown';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'account': return '#2196F3';
      case 'income': return '#26a69a';
      case 'expense': return '#ff7961';
      default: return '#999';
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#333' }}>Chart of Accounts</h3>
        <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
          Complete listing of all accounts and categories ordered by ledger code
        </p>
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '15px', 
        marginBottom: '20px',
        padding: '15px',
        background: '#f8f9fa',
        borderRadius: '8px',
        alignItems: 'center'
      }}>
        <div style={{ flex: 1 }}>
          <label style={{ 
            display: 'block', 
            fontSize: '11px', 
            fontWeight: 600, 
            color: '#666', 
            marginBottom: '6px',
            textTransform: 'uppercase'
          }}>
            Search by Code or Name
          </label>
          <input
            type="text"
            placeholder="Enter ledger code or account name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '2px solid #dee2e6',
              borderRadius: '6px',
              fontSize: '13px',
              boxSizing: 'border-box',
              transition: 'border-color 0.3s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#26a69a'}
            onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
          />
        </div>

        <div style={{ width: '200px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '11px', 
            fontWeight: 600, 
            color: '#666', 
            marginBottom: '6px',
            textTransform: 'uppercase'
          }}>
            Filter by Type
          </label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '2px solid #dee2e6',
              borderRadius: '6px',
              fontSize: '13px',
              boxSizing: 'border-box',
              cursor: 'pointer',
              background: 'white',
              transition: 'border-color 0.3s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#26a69a'}
            onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
          >
            <option value="all">All Types</option>
            <option value="account">Accounts Only</option>
            <option value="income">Income Only</option>
            <option value="expense">Expense Only</option>
          </select>
        </div>

        {(searchText || typeFilter !== 'all') && (
          <div style={{ alignSelf: 'flex-end' }}>
            <button
              onClick={() => {
                setSearchText('');
                setTypeFilter('all');
              }}
              style={{
                padding: '10px 16px',
                background: '#ff7961',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              onMouseOver={(e) => e.target.style.background = '#ff5252'}
              onMouseOut={(e) => e.target.style.background = '#ff7961'}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {filteredItems.length === 0 ? (
        <p className="empty-state" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
          {allItems.length === 0 ? 'No accounts or categories found.' : 'No items match your search criteria.'}
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={{ textAlign: 'left', padding: '12px 15px', fontSize: '11px', color: '#666', fontWeight: 600, textTransform: 'uppercase', borderBottom: '2px solid #dee2e6' }}>
                LEDGER CODE
              </th>
              <th style={{ textAlign: 'left', padding: '12px 15px', fontSize: '11px', color: '#666', fontWeight: 600, textTransform: 'uppercase', borderBottom: '2px solid #dee2e6' }}>
                NAME
              </th>
              <th style={{ textAlign: 'left', padding: '12px 15px', fontSize: '11px', color: '#666', fontWeight: 600, textTransform: 'uppercase', borderBottom: '2px solid #dee2e6' }}>
                TYPE
              </th>
              <th style={{ textAlign: 'left', padding: '12px 15px', fontSize: '11px', color: '#666', fontWeight: 600, textTransform: 'uppercase', borderBottom: '2px solid #dee2e6' }}>
                GROUP
              </th>
              <th style={{ textAlign: 'right', padding: '12px 15px', fontSize: '11px', color: '#666', fontWeight: 600, textTransform: 'uppercase', borderBottom: '2px solid #dee2e6' }}>
                BALANCE
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => {
              const balance = item.type === 'account' ? (balances[item.name] || 0) : 0;
              
              return (
                <tr key={`${item.type}-${item.id}`} style={{ transition: 'background 0.2s' }}>
                  <td style={{ 
                    padding: '12px 15px', 
                    borderBottom: '1px solid #f5f5f5', 
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    color: '#333'
                  }}>
                    {item.Ledger || '-'}
                  </td>
                  <td style={{ 
                    padding: '12px 15px', 
                    borderBottom: '1px solid #f5f5f5', 
                    fontSize: '14px',
                    fontWeight: 500
                  }}>
                    {item.name}
                  </td>
                  <td style={{ 
                    padding: '12px 15px', 
                    borderBottom: '1px solid #f5f5f5'
                  }}>
                    <span style={{
                      background: getTypeColor(item.type) + '15',
                      color: getTypeColor(item.type),
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase'
                    }}>
                      {getTypeLabel(item.type)}
                    </span>
                  </td>
                  <td style={{ 
                    padding: '12px 15px', 
                    borderBottom: '1px solid #f5f5f5', 
                    fontSize: '13px',
                    color: '#666'
                  }}>
                    {item.group || '-'}
                  </td>
                  <td style={{ 
                    padding: '12px 15px', 
                    borderBottom: '1px solid #f5f5f5', 
                    fontSize: '14px',
                    textAlign: 'right',
                    fontWeight: 600,
                    fontFamily: 'monospace',
                    color: item.type === 'account' ? (balance >= 0 ? '#26a69a' : '#ff5252') : '#999'
                  }}>
                    {item.type === 'account' ? `£${balance.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        background: '#f8f9fa', 
        borderRadius: '8px',
        fontSize: '12px',
        color: '#666'
      }}>
        <div style={{ marginBottom: '8px' }}>
          <strong>Showing:</strong> {filteredItems.length} of {allItems.length} items
          {(searchText || typeFilter !== 'all') && (
            <span style={{ marginLeft: '10px', color: '#26a69a', fontWeight: 600 }}>
              (Filtered)
            </span>
          )}
        </div>
        <div>
          <strong>Total:</strong> {allItems.length} 
          <span style={{ margin: '0 15px' }}>|</span>
          <strong>Accounts:</strong> {accounts.length}
          <span style={{ margin: '0 15px' }}>|</span>
          <strong>Income Categories:</strong> {incomeCategories.length}
          <span style={{ margin: '0 15px' }}>|</span>
          <strong>Expense Categories:</strong> {expenseCategories.length}
        </div>
      </div>
    </div>
  );
}

export default ChartOfAccounts;